"use client";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useRef, useState } from "react";
import useIsClient from "@/hooks/useIsClient";
import { logClientSideError } from "@/utils/logging/clientErrorLogger";

/**
 * Gives its children an `openGallery(index)` without loading a lightbox.
 *
 * PERF: `react-lightgallery` + `lightgallery.css` + its icon font is ~129KB
 * (measured: chunk `3442-*.js` 87,126B, plus 23,748B CSS and 18,813B of `lg.*`
 * fonts) for a lightbox most visitors never open. It used to be a static import
 * here, which put it on the PDP always — and on the HOME page too, because
 * `common-builder/gallery/Galleries.tsx` mounts this as well whenever a tenant
 * configures a gallery section.
 *
 * The public shape is unchanged — same props, same render-prop signature — so
 * neither call site's JSX changed. The library now arrives on first open:
 * `openGallery` arms the lazy host, the host reports back through `onReady`, and
 * the originally-requested index is replayed. That replay is load-bearing:
 * `Galleries` opens at `imagesUrl.findIndex(...)`, not at 0.
 *
 * NOTE — one deliberate DOM change. `LightgalleryItem` renders an empty `<div>`
 * per image; those used to sit in the caller's layout flow. In `Galleries` the
 * caller is a `<Row style={{gap:12}}>`, so N invisible items were each claiming
 * a 12px flex gap. They now live inside a `display:none` wrapper in the host, so
 * layout is identical before and after the lightbox loads — but it differs from
 * the old behaviour by that phantom spacing. Worth a look during visual QA.
 */

const LightgalleryHost = dynamic(() => import("./LightgalleryHost"), {
  ssr: false,
});

type OpenFn = (index?: number) => void;

/**
 * A lightbox is an enhancement: if the third-party gallery throws, the shopper
 * keeps the product page instead of the route-level error boundary. Still
 * reported, so it stays visible in Graylog.
 */
const safeOpen = (open: OpenFn, index?: number) => {
  try {
    open(index);
  } catch (error) {
    void logClientSideError(error as Error).catch(() => {});
  }
};

export type PreviewChildrenType = (
  openGallery: (index?: number) => void,
) => React.ReactNode;

const PreviewProvider: React.FC<{
  images: string[];
  children: PreviewChildrenType;
}> = ({ images, children }) => {
  const isClient = useIsClient();
  /**
   * The latest open request — a fresh object per call, so re-opening at the
   * same index still re-runs the effect below. Non-null also means "the host
   * should mount".
   */
  const [request, setRequest] = useState<{ index?: number } | null>(null);
  /** The real opener, published by the lazy host once the library is ready. */
  const [opener, setOpener] = useState<OpenFn | null>(null);
  /**
   * The request already handed to the opener. The host re-publishes its opener
   * whenever it remounts; without this the effect would replay the last request
   * then — re-opening a gallery the shopper already closed.
   */
  const servedRef = useRef<object | null>(null);

  const openGallery = useCallback((index?: number) => {
    setRequest({ index });
  }, []);

  // `setOpener(() => open)` — the callback form is required, or React would
  // treat the function as a state updater and call it.
  const handleReady = useCallback((open: OpenFn) => setOpener(() => open), []);

  useEffect(() => {
    if (!request || !opener || servedRef.current === request) return;
    servedRef.current = request;
    safeOpen(opener, request.index);
  }, [request, opener]);

  if (!isClient) return;
  return (
    <>
      {children(openGallery)}
      {request && <LightgalleryHost images={images} onReady={handleReady} />}
    </>
  );
};

export default PreviewProvider;
